import { type KinerjaProfil, type KinerjaProfilRole } from 'types/kinerja'

import type { KinerjaRoleType } from 'types/kinerja'

/**
 * Checks if a user has a specific role.
 * @param data - The user's profile data.
 * @param role - The role to check.
 * @returns Returns true if the user has the specified role, otherwise false.
 */
export const isUserHasRole = (data: KinerjaProfil, role: KinerjaRoleType) => {
  if (!data) return false

  return (
    data?.roles?.some((record: KinerjaProfilRole) => {
      return record.type === role
    }) || false
  )
}

/**
 * Checks if the user has the Kepala Sekolah Definitif role.
 * @param data - The KinerjaProfil data.
 * @returns A boolean indicating whether the user has the Kepala Sekolah Definitif role.
 */
export const isUserHasKsRole = (data: KinerjaProfil) => {
  return isUserHasRole(data, 'KS_DEFINITIF')
}

/**
 * Checks if the user has the Plt Kepala Sekolah role.
 * @param data - The KinerjaProfil data.
 * @returns A boolean indicating whether the user has the Plt Kepala Sekolah role.
 */
export const isUserHasPltRole = (data: KinerjaProfil) => {
  if (process.env.NEXT_PUBLIC_SKP_KS_PLT_ENABLED === 'true')
    return isUserHasRole(data, 'KS_PLT')

  return false
}

export const isKsDefinitifPlt = (data: KinerjaProfil) => {
  return isUserHasKsRole(data) || isUserHasPltRole(data)
}

/**
 * The function checks if a user has a hybrid role as a teacher and has a definitive KS or PLT status.
 * @param {KinerjaProfil} [data] - The `data` parameter is of type `KinerjaProfil`, which is likely an
 * object containing information about a user's profile.
 */
export const isUserHasHybridRole = (data?: KinerjaProfil) =>
  isUserHasRole(data, 'GURU') && isKsDefinitifPlt(data)

export const RoleLabel = {
  KS_PLT: 'Plt. Kepala Sekolah',
  KS_DEFINITIF: 'Kepala Sekolah Definitif',
  GURU: 'Guru',
}

export const RoleLabelShort = {
  KS_PLT: 'Plt. Kepala Sekolah',
  KS_DEFINITIF: 'Kepala Sekolah',
  GURU: 'Guru',
}
